import React from "react";

interface DocumentPreviewSectionProps {
  documentPreviewRef: React.RefObject<HTMLElement>;
  isMobile: boolean;
  handleSignUp: (e?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
}

interface DocumentTile {
  badge?: string;
  badgeClass?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  type?: 'functional' | 'cta-image';
  ctaText?: string;
  ctaUrl?: string;
  backgroundImage?: string;
}

const DocumentTileComponent: React.FC<{ tile: DocumentTile; index: number }> = React.memo(({ tile, index }) => {
  if (tile.type === 'cta-image') {
    return (
      <a href={tile.ctaUrl || "/freebie-downloadables"} className={`document-tile document-tile-cta ${tile.badgeClass || ''}`} style={{ backgroundImage: `url(${tile.backgroundImage})` }}>
        {tile.badge && <div className="tile-badge">{tile.badge}</div>}
      </a>
    );
  }

  return (
    <a href="/freebie-downloadables" className={`document-tile ${tile.badgeClass || ''}`}>
      {tile.badge && <div className="tile-badge">{tile.badge}</div>}
      <div className="tile-icon">{tile.icon}</div>
      <h3>{tile.title}</h3>
      <p>{tile.description}</p>
      <div className="tile-features">
        {tile.features.map((feature, i) => (
          <span key={i}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            {feature}
          </span>
        ))}
      </div>
    </a>
  );
});

const documentTiles: DocumentTile[] = [
  {
    badge: "MOST USED",
    badgeClass: "most-used",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
        <line x1="12" y1="11" x2="12" y2="17"></line>
        <line x1="9" y1="14" x2="15" y2="14"></line>
      </svg>
    ),
    title: "Teaching Programs",
    description: "Complete lesson plans and units of work aligned to Australian, Victorian or NSW curriculum",
    features: [
      "Lesson-by-lesson breakdown",
      "Curriculum codes and links",
      "Differentiation and assessment opportunities",
    ],
  },
  {
    badge: "NEW",
    badgeClass: "new",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="12" rx="1"></rect>
        <line x1="7" y1="8" x2="17" y2="8"></line>
        <line x1="7" y1="12" x2="13" y2="12"></line>
        <rect x="8" y="18" width="8" height="2" rx="1"></rect>
        <path d="M12 16v2"></path>
      </svg>
    ),
    title: "Slideshow Presentations",
    description: "Instant and editable, ready to teach! Just drag and drop your lesson plan or unit of work.",
    features: [
      "AI-powered presentation generation",
      "Professional slide templates",
      "Drag and drop integration",
    ],
  },
  {
    type: 'cta-image',
    icon: <></>,
    title: "",
    description: "",
    features: [],
    ctaUrl: "/dashboard#create-slides",
    backgroundImage: "/images/custom-slideshow-cta.png"
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    ),
    title: "Individual Plans",
    description: "SMART goals and strategies for personalised learning and behaviour support",
    features: [
      "Evidence-based strategy suggestions",
      "Completely custom-made",
      "Fully formatted and ready to use",
    ],
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <line x1="12" y1="11" x2="12" y2="17"></line>
        <line x1="9" y1="14" x2="15" y2="14"></line>
      </svg>
    ),
    title: "Report Comments",
    description: "Personalised comments, general or subject-specific",
    features: [
      "Student-specific comments",
      "Tick and fill - we'll take care of the prose",
      "No student data stored",
    ],
  },
  {
    badge: "BETA",
    badgeClass: "beta",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <line x1="12" y1="11" x2="12" y2="17"></line>
        <line x1="9" y1="14" x2="15" y2="14"></line>
      </svg>
    ),
    title: "Student assessments or quizzes",
    description: "Custom worksheets to check student understanding",
    features: [
      "Select multiple choice, short answer or long answer",
      "Curriculum aligned and informed",
      "Fully editable",
    ],
  },
];

export const DocumentPreviewSection: React.FC<DocumentPreviewSectionProps> = ({
  documentPreviewRef,
  isMobile,
  handleSignUp,
}) => {
  const [currentDocumentTile, setCurrentDocumentTile] = React.useState(0);
  const totalDocumentTiles = documentTiles.length;
  const tilesPerPage = isMobile ? 1 : 3;
  const totalPages = Math.ceil(totalDocumentTiles / tilesPerPage);

  const touchStartRef = React.useRef<number | null>(null);
  const documentCarouselRef = React.useRef<HTMLDivElement>(null);

  const nextDocumentTile = React.useCallback(() => {
    setCurrentDocumentTile((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevDocumentTile = React.useCallback(() => {
    setCurrentDocumentTile((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = React.useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStartRef.current - touchEnd;

    // Minimum swipe distance
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        // Swipe left - next tile
        nextDocumentTile();
      } else {
        // Swipe right - previous tile
        prevDocumentTile();
      }
    }

    touchStartRef.current = null;
  }, [nextDocumentTile, prevDocumentTile]);

  return (
    <section className="document-preview-section" ref={documentPreviewRef}>
      <div className="document-preview-content">
        <div className="section-header">
          <h2>Use custom AI to make ready-to-use...</h2>
        </div>

        <div className="document-tiles-carousel">
          {/* Carousel arrows */}
          <button className="carousel-arrow carousel-arrow-left" onClick={prevDocumentTile}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button className="carousel-arrow carousel-arrow-right" onClick={nextDocumentTile}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          <div
            className="document-tiles-container"
            ref={documentCarouselRef}
            onTouchStart={isMobile ? handleTouchStart : undefined}
            onTouchEnd={isMobile ? handleTouchEnd : undefined}
          >
            <div
              className="document-tiles-track"
              style={{
                transform: isMobile
                  ? `translateX(-${currentDocumentTile * 16.67}%)`
                  : `translateX(-${currentDocumentTile * 50}%)`,
                transition: 'transform 0.3s ease-out'
              }}
            >
              {documentTiles.map((tile, index) => (
                <DocumentTileComponent key={index} tile={tile} index={index} />
              ))}
            </div>
          </div>

          {/* Carousel dots */}
          <div className="carousel-dots document-carousel-dots">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentDocumentTile ? 'active' : ''}`}
                onClick={() => setCurrentDocumentTile(index)}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Dual CTA - Browse samples or start trial */}
        <div className="dual-cta-section">
          <div className="cta-option">
            <p>Want to see a sample?</p>
            <a href="/freebie-downloadables" className="sample-cta-link">
              TAKE ME TO THE FREEBIE DOWNLOADABLES
            </a>
          </div>
          <div className="cta-option">
            <p>Want to custom-create your own?</p>
            <button onClick={handleSignUp} className="trial-cta-button">
              TRY FOR FREE
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};